import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppLayout from "../components/AppLayout";
import { ReminderSettings } from "../components/ReminderBanner";
import { toast } from "sonner";
import { User, Target, LogOut, RefreshCw, Mail, Shield, CheckCircle2, Trash2, PlayCircle, Loader2, X, AlertTriangle } from "lucide-react";

export default function Settings() {
  const { user, refreshUser, logout, supabase } = useAuth();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [busyGoal, setBusyGoal] = useState(null); // goal_id currently being mutated
  const [confirmDelete, setConfirmDelete] = useState(null); // goal object pending confirmation
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(true);

  const loadGoals = useCallback(async () => {
    try {
      const { data } = await api.get("/goals");
      setGoals(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadGoals(); }, [loadGoals]);

  const handleLogout = () => { logout(); navigate("/"); };

  const deleteAccount = async () => {
    if (confirmText.trim().toLowerCase() !== "delete") return;
    setDeletingAccount(true);
    try {
      await api.delete("/auth/me");
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      toast.success("Your account has been deleted.");
      // Hard redirect to landing to fully clear any cached state
      window.location.replace("/");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDeletingAccount(false);
    }
  };

  const activeGoalId = user?.active_goal_id;

  const activateGoal = async (goal) => {
    if (goal.goal_id === activeGoalId) return;
    setBusyGoal(goal.goal_id);
    try {
      await api.post(`/goals/${goal.goal_id}/activate`);
      await refreshUser();
      await loadGoals();
      toast.success(`Now tracking: ${goal.role_target}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyGoal(null); }
  };

  const deleteGoal = async () => {
    if (!confirmDelete) return;
    const g = confirmDelete;
    setBusyGoal(g.goal_id);
    try {
      await api.delete(`/goals/${g.goal_id}`);
      setConfirmDelete(null);
      await refreshUser();
      await loadGoals();
      toast.success(`Deleted goal: ${g.role_target}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusyGoal(null); }
  };

  return (
    <AppLayout>
      <div className="mb-8">
        <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Account</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-extrabold mt-1">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="glass rounded-2xl p-6" data-testid="profile-card">
          <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase font-semibold mb-4"><User className="w-4 h-4" /> Profile</div>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-xl font-semibold">{user?.name?.[0]?.toUpperCase() || "U"}</div>
            <div>
              <div className="font-heading text-lg font-semibold">{user?.name}</div>
              <div className="text-sm text-zinc-400 flex items-center gap-1"><Mail className="w-3 h-3" /> {user?.email}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-sm text-zinc-400"><Shield className="w-4 h-4 text-primary" /> Role: <span className="text-white capitalize">{user?.role}</span></div>
        </div>

        <div className="glass rounded-2xl p-6" data-testid="goals-card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase font-semibold"><Target className="w-4 h-4" /> Your goals</div>
            <span className="text-xs text-zinc-500">{goals.length} total</span>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : goals.length === 0 ? (
            <p className="text-sm text-zinc-400">No goals yet.</p>
          ) : (
            <div className="space-y-2" data-testid="goals-list">
              {goals.map((g) => {
                const isActive = g.goal_id === activeGoalId;
                const busy = busyGoal === g.goal_id;
                return (
                  <div
                    key={g.goal_id}
                    data-testid={`goal-row-${g.goal_id}`}
                    className={`flex items-center justify-between gap-3 border rounded-xl p-3 transition-colors ${isActive ? "border-primary/50 bg-primary/5" : "border-white/10 hover:border-white/20"}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-sm font-medium truncate">{g.role_target}</div>
                        {isActive && (
                          <span className="text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-full text-primary bg-primary/15 flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Active
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {g.user_chosen_days || g.ai_recommended_days} days · <span className="capitalize">{g.status}</span>
                        {g.target_salary ? <> · {g.target_salary}</> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!isActive && (
                        <button
                          data-testid={`activate-goal-${g.goal_id}`}
                          onClick={() => activateGoal(g)}
                          disabled={busy}
                          className="inline-flex items-center gap-1.5 text-xs bg-primary/90 hover:bg-primary text-black px-3 py-1.5 rounded-lg font-medium disabled:opacity-60"
                          title="Switch to this goal"
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                          Switch
                        </button>
                      )}
                      <button
                        data-testid={`delete-goal-${g.goal_id}`}
                        onClick={() => setConfirmDelete(g)}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 text-xs border border-red-500/30 text-red-400 hover:bg-red-500/10 px-3 py-1.5 rounded-lg font-medium disabled:opacity-60"
                        title="Delete this goal"
                        aria-label="Delete goal"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <button data-testid="change-goal-btn" onClick={() => navigate("/onboarding")}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 py-2.5 rounded-xl font-medium transition-colors">
            <RefreshCw className="w-4 h-4" /> Set a new goal
          </button>
        </div>
      </div>

      <div className="mt-5">
        <ReminderSettings />
      </div>

      <button data-testid="settings-logout-btn" onClick={handleLogout}
        className="mt-6 inline-flex items-center gap-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 px-5 py-2.5 rounded-xl font-medium transition-colors">
        <LogOut className="w-4 h-4" /> Log out
      </button>

      {/* Danger zone */}
      <div className="mt-10 rounded-2xl border border-red-500/25 bg-red-500/[0.03] p-6" data-testid="danger-zone">
        <div className="flex items-center gap-2 text-red-400 text-xs tracking-wider uppercase font-semibold mb-2">
          <AlertTriangle className="w-4 h-4" /> Danger zone
        </div>
        <h3 className="font-heading text-lg font-extrabold tracking-tighter">Delete account</h3>
        <p className="text-sm text-zinc-400 mt-1 max-w-xl">
          Permanently erase your account and everything on it — goals, roadmaps, task history, resumes, interviews, feedback. This can&apos;t be undone.
        </p>
        <button
          data-testid="delete-account-btn"
          onClick={() => { setConfirmText(""); setConfirmDeleteAccount(true); }}
          className="mt-4 inline-flex items-center gap-2 bg-red-500/90 hover:bg-red-500 text-white px-5 py-2.5 rounded-xl font-medium transition-colors"
        >
          <Trash2 className="w-4 h-4" /> Delete my account
        </button>
      </div>

      {/* Confirm delete modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => !busyGoal && setConfirmDelete(null)}>
          <div
            className="w-full sm:max-w-md rounded-2xl p-6 border border-white/10"
            style={{ background: "rgba(15,15,15,0.98)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-confirm-modal"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-extrabold tracking-tighter">Delete this goal?</h3>
                  <p className="text-sm text-zinc-400 mt-1">
                    <span className="text-white font-medium">{confirmDelete.role_target}</span> — this will also delete its roadmap, all daily tasks and progress. This can&apos;t be undone.
                  </p>
                </div>
              </div>
              <button onClick={() => !busyGoal && setConfirmDelete(null)} className="text-zinc-500 hover:text-white" aria-label="Cancel">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={!!busyGoal}
                className="px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-delete-btn"
                onClick={deleteGoal}
                disabled={!!busyGoal}
                className="inline-flex items-center gap-2 bg-red-500/90 hover:bg-red-500 text-white px-5 py-2.5 rounded-xl font-medium disabled:opacity-60 transition-colors"
              >
                {busyGoal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete goal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete-account confirm modal */}
      {confirmDeleteAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => !deletingAccount && setConfirmDeleteAccount(false)}>
          <div
            className="w-full sm:max-w-md rounded-2xl p-6 border border-red-500/30"
            style={{ background: "rgba(15,10,10,0.98)" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="delete-account-modal"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-extrabold tracking-tighter">Delete your account permanently?</h3>
                  <p className="text-sm text-zinc-400 mt-1">
                    <span className="text-white font-medium">{user?.email}</span> — every goal, roadmap, task, streak, resume and interview will be erased. There&apos;s no going back.
                  </p>
                </div>
              </div>
              <button onClick={() => !deletingAccount && setConfirmDeleteAccount(false)} className="text-zinc-500 hover:text-white" aria-label="Cancel">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-5">
              <label className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Type <span className="text-red-400 font-mono">DELETE</span> to confirm</label>
              <input
                data-testid="delete-account-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                autoFocus
                placeholder="DELETE"
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-red-500 text-white"
              />
            </div>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setConfirmDeleteAccount(false)} disabled={deletingAccount} className="px-4 py-2 text-sm text-zinc-300 hover:text-white transition-colors">Cancel</button>
              <button
                data-testid="confirm-delete-account-btn"
                onClick={deleteAccount}
                disabled={deletingAccount || confirmText.trim().toLowerCase() !== "delete"}
                className="inline-flex items-center gap-2 bg-red-500/90 hover:bg-red-500 text-white px-5 py-2.5 rounded-xl font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {deletingAccount ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete account
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
